export class Accordion {

    static #instances = new Map();
    static #globalEventListenersAdded = false;

    static {
        if (!Accordion.#globalEventListenersAdded) {
            Accordion.#addGlobalEventListeners();
            Accordion.#globalEventListenersAdded = true;
        }
    }

    constructor(settings = {}) {
        const {
            accordion,
            singleItem = true,
            allowDeeplinking = false,
            on = {}
        } = settings;

        if (typeof accordion === 'string') {
            this.accordion = document.querySelector(accordion);
        } else if (accordion instanceof HTMLElement) {
            this.accordion = accordion;
        } else {
            console.warn('Invalid accordion parameter. Expected string or HTMLElement.');
            return;
        }

        if (!this.accordion) {
            console.warn(`Accordion element not found for selector or element: ${accordion}`);
            return;
        }

        if (Accordion.#instances.has(this.accordion)) {
            console.warn('Accordion instance already exists for the provided element.');
            return;
        }

        this.singleItem = singleItem;
        this.allowDeeplinking = allowDeeplinking;
        this.items = Array.from(this.accordion.querySelectorAll('.accordion-item'));

        if (!this.items.length) {
            console.warn(`Accordion container "${accordion}" has no items.`);
            return;
        }

        this.on = {
            open: on.open || (() => {}),
            close: on.close || (() => {}),
        };

        this.isTriggered = false;
        Accordion.#instances.set(this.accordion, this);

        this.accordion.classList.add('no-transition');
        this.init();
        requestAnimationFrame(() => {
            this.accordion.classList.remove('no-transition');
        });
    }

    init() {
        this.addEventListener();

        if (this.allowDeeplinking) {
            Accordion.#historyStateHandler();
        }
    }

    addEventListener() {
        this.items.forEach((item, index) => {
            const button = item.querySelector('.accordion-button');
            const panel = item.querySelector('.accordion-panel');

            if (!button || !panel) {
                console.warn('Accordion item missing required button or panel.');
                return;
            }

            item.button = button;
            item.panel = panel;
            item.dataset.index = index;

            item.button.addEventListener('click', (event) => {
                this.toggleItem(event, true);
            }, true);

            item.button.addEventListener('keydown', (event) => {
                this.handleKeydown(event);
            }, true);
        });
    }

    toggleItem(event, jump = false) {
        event.preventDefault();
        const item = event.currentTarget.closest('.accordion-item');
        if (!item) return;

        const isExpanded = item.button.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
            this.closeItem(item);
        } else {
            if (this.singleItem) {
                this.closeAllItems();
            }
            this.openItem(item, jump);
        }

        this.setHistoryState();
    }

    openItem(item, jump = false) {
        if (item.panel.hidden) {
            item.button.setAttribute('aria-expanded', 'true');
            item.panel.hidden = false;

            if (typeof this.on.open === 'function') this.on.open.call(this, item, this);

            if (jump) {
                const scrollPos = window.scrollY;
                const rect = this.accordion.getBoundingClientRect();
                const pos = scrollPos + rect.top;
                
                const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--site-header-height')) || 96;
                const offset = headerHeight + item.offsetHeight;
                window.scrollTo({
                    top: pos - offset,
                    behavior: 'smooth'
                });
            }

        }
    }

    closeItem(item) {
        if (!item.panel.hidden) {
            item.button.setAttribute('aria-expanded', 'false');
            item.panel.hidden = true;

            if (typeof this.on.close === 'function') this.on.close.call(this, item, this);
        }
    }

    closeAllItems() {
        this.items.forEach(item => this.closeItem(item));
    }

    handleKeydown(event) {
        const currentItem = event.currentTarget.closest('.accordion-item');
        const { key } = event;
        const currentIndex = this.items.indexOf(currentItem);
        let newIndex = null;

        switch (key) {
            case 'ArrowDown':
            case 'ArrowRight':
                newIndex = (currentIndex + 1) % this.items.length;
                break;
            case 'ArrowUp':
            case 'ArrowLeft':
                newIndex = (currentIndex - 1 + this.items.length) % this.items.length;
                break;
            case 'Home':
                newIndex = 0;
                break;
            case 'End':
                newIndex = this.items.length - 1;
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                this.toggleItem(event);
                return;
            default:
                return;
        }

        if (newIndex !== null) {
            event.preventDefault();
            this.items[newIndex].querySelector('.accordion-button').focus();
        }
    }

    setHistoryState() {
        if (window.HashManager && this.allowDeeplinking) {
            this.isTriggered = true;
            const openItems = this.items.filter(item => item.button.getAttribute('aria-expanded') === 'true');
            const identifiers = [...new Set(openItems.map(item => encodeURIComponent(item.id || item.dataset.index)))];
            window.HashManager.updateState( {
                [this.accordion.id]: identifiers.length >= 1 ? identifiers : false
            });
        }
    }

    static #addGlobalEventListeners() {
        window.addEventListener('popstate', Accordion.#debounce(Accordion.#historyStateHandler, 100));
    }

    static #historyStateHandler() {
        if (window.HashManager) {
            const states = window.HashManager.state;
            Accordion.#instances.forEach(instance => {
                if (!instance.isTriggered && instance.allowDeeplinking) {
                    if (Object.prototype.hasOwnProperty.call(states, instance.accordion.id)) {
                        const activeItems = [...states[instance.accordion.id]];
                        instance.items.forEach((item, index) => {
                            instance.closeItem(item);
                            if (activeItems.includes(String(index))) {
                                instance.openItem(item);
                            }
                        });
                    } else {
                        instance.items.forEach(item => {
                            if (item.matches('[aria-expanded="false"]')) {
                                instance.closeItem(item);
                            }
                        });
                    }
                }
                if (instance.isTriggered) instance.isTriggered = false;
            });
        }
    }

    static #debounce(func, delay) {
        let timer;
        return function(...args) {
            clearTimeout(timer);
            timer = setTimeout(() => func.apply(this, args), delay);
        };
    }

}